import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Spinner, Card } from '../ui';
import { adminApi, routeApi } from '../../services/api';
import BaseModal from './BaseModal';
import { toast } from 'sonner';

const CreateRouteModal = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [mahallas, setMahallas] = useState([]);
  
  const [searchMahalla, setSearchMahalla] = useState('');
  
  const [form, setForm] = useState({
    driverId: '',
    scheduledDate: new Date().toISOString().split('T')[0],
    mahallaIds: []
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [usersData, mahallasData] = await Promise.all([
        adminApi.getUsers(),
        adminApi.getMahallas()
      ]);
      // Filter only drivers using role or isDriver property
      setDrivers(usersData.filter(u => u.isDriver || u.role?.toUpperCase() === 'DRIVER'));
      setMahallas(mahallasData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error(t('adminRoutes.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredMahallas = mahallas.filter(m => 
    m.name.toLowerCase().includes(searchMahalla.toLowerCase())
  );

  const toggleMahalla = (id) => {
    setForm(prev => ({
      ...prev,
      mahallaIds: prev.mahallaIds.includes(id)
        ? prev.mahallaIds.filter(mid => mid !== id)
        : [...prev.mahallaIds, id]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.driverId) return toast.error(t('createRouteModal.selectDriverError'));
    if (form.mahallaIds.length === 0) return toast.error(t('createRouteModal.selectMahallaError'));

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        scheduledDate: new Date(form.scheduledDate).toISOString()
      };
      await routeApi.create(payload);
      toast.success(t('createRouteModal.created'));
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.message || t('setPassword.genericError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseModal onClose={onClose} title={t('createRouteModal.title')} subtitle={t('createRouteModal.subtitle')}>
      {loading ? (
        <div className="py-20 flex justify-center">
          <Spinner />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6 py-2">
          {/* Driver Selection */}
          <div className="space-y-3">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] px-1 italic">{t('createRouteModal.step1')}</label>
            {drivers.length === 0 ? (
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border-2 border-dashed border-slate-200 dark:border-slate-800 text-center">
                <p className="text-[10px] font-black uppercase text-slate-400 italic">{t('createRouteModal.noDrivers')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[180px] overflow-y-auto pr-1 custom-scrollbar">
                {drivers.map(driver => (
                  <div 
                    key={driver.id}
                    onClick={() => setForm(f => ({ ...f, driverId: driver.id }))}
                    className={`p-3 rounded-2xl border-2 transition-all cursor-pointer flex items-center gap-3 ${
                      form.driverId === driver.id 
                        ? 'border-[#1e9f6b] bg-[#1e9f6b]/5' 
                        : 'border-slate-50 dark:border-slate-800 hover:border-slate-100'
                    }`}
                  >
                    <div className={`h-8 w-8 rounded-xl flex items-center justify-center font-black italic text-xs ${
                      form.driverId === driver.id ? 'bg-[#1e9f6b] text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
                    }`}>
                      {driver.name?.[0] || driver.fullName?.[0] || driver.firstName?.[0] || 'D'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-black italic uppercase tracking-tight text-slate-900 dark:text-white truncate">
                        {driver.name || driver.fullName || `${driver.firstName} ${driver.lastName}`}
                      </p>
                      <p className="text-[8px] font-bold text-slate-400 tracking-widest">{driver.phone}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Date & Mahalla Selection Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-3">
               <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] px-1 italic">{t('createRouteModal.step2')}</label>
               <Input
                 type="date"
                 value={form.scheduledDate}
                 onChange={e => setForm(f => ({ ...f, scheduledDate: e.target.value }))}
                 className="h-12 font-black italic text-xs"
               />
            </div>
            <div className="space-y-3">
               <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] px-1 italic">{t('createRouteModal.step3')}</label>
               <Input
                 placeholder={t('createRouteModal.mahallaPlaceholder')}
                 icon="search"
                 value={searchMahalla}
                 onChange={e => setSearchMahalla(e.target.value)}
                 className="h-12 font-black italic text-xs"
               />
            </div>
          </div>

          {/* Mahalla Selection */}
          <div className="space-y-3">
            <div className="flex justify-between items-center px-1">
               <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] italic">{t('createRouteModal.step4', { count: form.mahallaIds.length })}</label>
               {form.mahallaIds.length > 0 && (
                  <button type="button" onClick={() => setForm(f => ({ ...f, mahallaIds: [] }))} className="text-[9px] font-black text-red-500 uppercase tracking-widest hover:underline italic">{t('createRouteModal.clear')}</button>
               )}
            </div>

            <Card className="p-4 bg-slate-50/50 dark:bg-slate-900/30 border-slate-100 dark:border-slate-800">
               <div className="max-h-[220px] overflow-y-auto pr-2 custom-scrollbar">
                  {filteredMahallas.length === 0 ? (
                    <div className="py-10 text-center">
                       <p className="text-[10px] font-black uppercase text-slate-400 italic">{t('createRouteModal.noMahallas')}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {filteredMahallas.map(m => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => toggleMahalla(m.id)}
                          className={`px-3 py-2.5 rounded-xl text-[9px] font-black uppercase italic tracking-tight text-left transition-all flex items-center gap-2 ${
                            form.mahallaIds.includes(m.id)
                              ? 'bg-[#1e9f6b] text-white shadow-lg shadow-[#1e9f6b]/20'
                              : 'bg-white dark:bg-slate-800 text-slate-500 border border-slate-100 dark:border-slate-700 hover:border-[#1e9f6b]/30'
                          }`}
                        >
                          <div className={`h-1.5 w-1.5 rounded-full ${form.mahallaIds.includes(m.id) ? 'bg-white' : 'bg-slate-300'}`} />
                          <span className="truncate">{m.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
               </div>
            </Card>
          </div>

          <div className="pt-2">
            <Button 
              type="submit"
              loading={submitting}
              className="w-full h-14 rounded-2xl font-black italic uppercase tracking-widest text-xs shadow-xl shadow-[#1e9f6b]/20"
            >
              {t('createRouteModal.submit')}
            </Button>
          </div>
        </form>
      )}
    </BaseModal>
  );
};

export default CreateRouteModal;
